/**
 * Journal Inside Carousel - Web Component
 * Custom element for managing the interactive carousel functionality
 */

if (!customElements.get('journal-inside-carousel')) {
  class JournalInsideCarousel extends HTMLElement {
    constructor() {
      super();

      // DOM elements
      this.carousel = this.querySelector('[data-carousel]');
      this.track = this.querySelector('.carousel__track');
      this.slides = this.querySelectorAll('[data-slide]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.descriptionContainer = this.querySelector('[data-description-container]');
      this.timeline = this.querySelector('[data-timeline]');
      this.timelineItems = this.querySelectorAll('[data-timeline-item]');

      // State
      this.currentIndex = 0;
      this.slideOffset = 0;
      this.totalSlides = this.slides.length;
      this.isAnimating = false;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.isInitialized = false;
    }

    connectedCallback() {
      if (!this.carousel || this.slides.length === 0) return;

      // Lazy initialization with IntersectionObserver (Performance optimization)
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.isInitialized) {
              this.init();
              this.intersectionObserver.unobserve(this);
            }
          });
        }, { rootMargin: '50px' });

        this.intersectionObserver.observe(this);
      } else {
        // Fallback for older browsers - initialize immediately
        this.init();
      }
    }

    disconnectedCallback() {
      // Cleanup ResizeObserver
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      // Cleanup IntersectionObserver
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    /**
     * Initialize the carousel
     */
    init() {
      if (this.isInitialized) return;

      this.isInitialized = true;

      // Initialize layout calculations
      this.calculateLayout();

      // Setup event listeners
      this.setupNavigationListeners();
      this.setupKeyboardNavigation();
      this.setupScrollListener();
      this.setupTimelineNavigation();
      this.setupResizeObserver();

      // Set initial state
      this.updateState();
      this.updateTimeline();
      this.updateFocusManagement();
    }

    /**
     * Calculate layout dimensions for slides
     */
    calculateLayout() {
      if (this.slides.length === 0) return;

      // Get the offset between first and second slide to determine slide width
      const visibleSlides = Array.from(this.slides).filter(slide => slide.clientWidth > 0);
      if (visibleSlides.length > 1) {
        this.slideOffset = visibleSlides[1].offsetLeft - visibleSlides[0].offsetLeft;
      } else if (visibleSlides.length === 1) {
        this.slideOffset = visibleSlides[0].clientWidth;
      }

      this.totalSlides = visibleSlides.length;
    }

    /**
     * Navigate to previous or next slide
     * @param {string} direction - 'prev' or 'next'
     */
    navigate(direction) {
      if (this.isAnimating || this.totalSlides <= 1) return;

      this.isAnimating = true;

      // Calculate target index
      let targetIndex;
      if (direction === 'next') {
        targetIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
      } else {
        targetIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
      }

      // Optimistically update UI to target state (instant feedback)
      this.currentIndex = targetIndex;
      this.updateDescription();
      this.updateTimeline();
      this.updateFocusManagement();

      // Then trigger scroll animation in background
      const newPosition = this.slideOffset * targetIndex;
      this.track.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });

      // Unlock after animation completes
      setTimeout(() => {
        this.isAnimating = false;
      }, 400);
    }

    /**
     * Update current state based on scroll position
     */
    updateState() {
      if (!this.track || this.slideOffset === 0) return;

      // Calculate current index from scroll position
      const newIndex = Math.round(this.track.scrollLeft / this.slideOffset);

      if (newIndex !== this.currentIndex) {
        this.currentIndex = newIndex;
        this.updateDescription();
        this.updateTimeline();
        this.updateFocusManagement();

        // Dispatch custom event for slide change
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            index: newIndex,
            slide: this.slides[newIndex],
            total: this.totalSlides
          }
        }));
      }
    }

    /**
     * Update the displayed description for current slide
     */
    updateDescription() {
      if (!this.descriptionContainer) return;

      const currentSlide = this.slides[this.currentIndex];
      if (!currentSlide) return;

      // Get the slide index
      const slideIndex = parseInt(currentSlide.dataset.slideIndex, 10);

      // Find the description element for this slide
      const allDescriptions = this.querySelectorAll('[data-description]');

      // Update active state for all descriptions
      allDescriptions.forEach(desc => {
        const descIndex = parseInt(desc.dataset.index, 10);
        const isActive = (descIndex === slideIndex);
        desc.setAttribute('data-active', isActive.toString());
      });
    }

    /**
     * Setup navigation button event listeners
     */
    setupNavigationListeners() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.navigate('prev');
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.navigate('next');
        });
      }
    }

    /**
     * Setup keyboard navigation (arrow keys)
     */
    setupKeyboardNavigation() {
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigate('prev');
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate('next');
        }
      });

      // Make carousel focusable for keyboard navigation
      if (this.carousel && !this.carousel.hasAttribute('tabindex')) {
        this.carousel.setAttribute('tabindex', '0');
      }
    }

    /**
     * Setup scroll event listener to track position
     */
    setupScrollListener() {
      let scrollTimeout;

      this.track.addEventListener('scroll', () => {
        // Debounce scroll events
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (!this.isAnimating) {
            this.updateState();
          }
        }, 100);
      });
    }

    /**
     * Setup timeline navigation - click to jump to slide
     */
    setupTimelineNavigation() {
      if (!this.timelineItems || this.timelineItems.length === 0) return;

      this.timelineItems.forEach((item) => {
        item.addEventListener('click', () => {
          if (this.isAnimating) return;

          const targetIndex = parseInt(item.dataset.slideIndex, 10);
          if (isNaN(targetIndex) || targetIndex === this.currentIndex) return;

          this.isAnimating = true;

          // Optimistically update UI to target state (instant feedback)
          this.currentIndex = targetIndex;
          this.updateDescription();
          this.updateTimeline();
          this.updateFocusManagement();

          // Then trigger scroll animation in background
          const targetPosition = this.slideOffset * targetIndex;
          this.track.scrollTo({
            left: targetPosition,
            behavior: 'smooth'
          });

          // Unlock after animation completes
          setTimeout(() => {
            this.isAnimating = false;
          }, 400);
        });
      });
    }

    /**
     * Update timeline indicators - highlight current slide
     */
    updateTimeline() {
      if (!this.timelineItems || this.timelineItems.length === 0) return;

      this.timelineItems.forEach((item, index) => {
        const isActive = (index === this.currentIndex);

        // Update aria-current for screen readers
        item.setAttribute('aria-current', isActive ? 'step' : 'false');

        // Toggle active class
        if (isActive) {
          item.classList.add('timeline-step--active');
        } else {
          item.classList.remove('timeline-step--active');
        }
      });

      // Scroll active timeline item into view (for mobile)
      const activeItem = this.timelineItems[this.currentIndex];
      if (activeItem && this.timeline) {
        activeItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }

    /**
     * Setup ResizeObserver for responsive layout recalculation
     */
    setupResizeObserver() {
      if (!('ResizeObserver' in window)) return;

      let resizeTimeout;

      this.resizeObserver = new ResizeObserver(() => {
        // Debounce resize events (150ms)
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.calculateLayout();
          this.updateState();
        }, 150);
      });

      this.resizeObserver.observe(this.carousel);
    }

    /**
     * Update focus management for visible/hidden slides (Accessibility)
     */
    updateFocusManagement() {
      if (!this.slides || this.slides.length === 0) return;

      this.slides.forEach((slide, index) => {
        const isVisible = (index === this.currentIndex);

        // Make only visible slide focusable
        slide.setAttribute('tabindex', isVisible ? '0' : '-1');

        // Update inert attribute for better accessibility
        if ('inert' in slide) {
          slide.inert = !isVisible;
        }
      });
    }
  }

  customElements.define('journal-inside-carousel', JournalInsideCarousel);
}
